import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthService, ROLES } from 'src/app/auth/auth.service';
import { CookiesService } from 'src/app/auth/cookies.service';
import { UnidadeDeSaude } from 'src/app/models/unidade-de-saude.model';
import { HealthMemberService } from 'src/app/services/health-member.service';
import { SidebarStateService } from 'src/app/services/sidebar-state.service';
import { SnackbarCustomService } from 'src/app/services/snackbar-custom.service';
import { captureError } from 'src/app/shared/util/error-capture.util';
import { environment } from 'src/environments/environment';
import { SNACK_MESSAGES, SNACK_TYPES } from '../messages-constants';
import { getAuthErrorMessage } from '../util/check-auth-error.util';
import { navRouterSwitch } from '../util/export-functions/nav-router-switch';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  isLoading = false;
  roles = ROLES;
  isCollapsed = false;

  userInactive: Subject<any> = new Subject();
  private destroy$ = new Subject<void>();
  institutions: UnidadeDeSaude[] = [];
  selectedInstitutionId: number;

  constructor(
    public authService: AuthService,
    public healthMemberService: HealthMemberService,
    private router: Router,
    private snackBar: SnackbarCustomService,
    private cookiesService: CookiesService,
    private sidebarStateService: SidebarStateService
  ) { }

  private _previousLoggedInState = false;

  ngOnInit() {
    this.isCollapsed = this.sidebarStateService.collapsed;

    if (!this.authService.checkUserRole(this.roles.PATIENT)) {
      if (this.isLoggedIn) {
        this.populateInstitutionsList();
      }
      this._previousLoggedInState = this.isLoggedIn;

      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        filter(() => {
          const currentLoggedIn = this.isLoggedIn;
          const stateChanged = this._previousLoggedInState !== currentLoggedIn;
          this._previousLoggedInState = currentLoggedIn;
          return stateChanged && currentLoggedIn;
        }),
        takeUntil(this.destroy$)
      ).subscribe(() => {
        if (
          this.isLoggedIn &&
          !this.authService.checkUserRole(this.roles.PATIENT)
        ) {
          this.populateInstitutionsList();
        }
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    this.sidebarStateService.setCollapsed(this.isCollapsed);
  }

  navbarRouter(path: string) {
    return this.router.navigate([navRouterSwitch(path)]);
  }

  isNavbarItemActive(itemPath: string): boolean {
    const targetPath = navRouterSwitch(itemPath);
    const currentUrl = this.router.url.split('?')[0];

    if (targetPath === '/app/patient') {
      return currentUrl === targetPath;
    }
    if (targetPath) {
      return currentUrl.includes(targetPath);
    }
    return itemPath ? currentUrl.includes(itemPath) : false;
  }

  async goToTamisBot() {
    if (await this.authService.haveSetToken()) {
      const token = await this.authService.getValidAccessToken();
      const baseUrl = environment.tamisBotUrl || 'www.google.com';

      if (token) {
        const params = new URLSearchParams({
          token: token
        });
        const dashboardUrl = `${baseUrl}?${params.toString()}`;
        window.open(dashboardUrl, '_blank');
      }
    }
  }

  async signOut(): Promise<void> {
    await this.authService.signOut();
    await this.router.navigate(['signIn']);
  }

  async populateInstitutionsList() {
    if (this.authService.checkUserRole(this.roles.PATIENT)) {
      return;
    }

    if (this.institutions.length > 0) {
      return;
    }

    try {
      this.isLoading = true;
      const { institutions } = await this.healthMemberService.getHealthMemberInstitutionList();
      this.institutions = institutions;

      const savedInstitutionId = Number(this.cookiesService.getCookie('institutionId'));
      if (savedInstitutionId) {
        const savedInstitution = this.institutions.find(inst => inst.id === savedInstitutionId);
        if (savedInstitution) {
          this.selectedInstitutionId = savedInstitution.id;
        }
      }
    } catch (error) {
      if (getAuthErrorMessage(error)) {
        this.snackBar.openSnackBar(
          SNACK_MESSAGES.ERROR.SESSION_EXPIRED,
          SNACK_TYPES.ERROR
        );
      } else {
        this.snackBar.openSnackBar(
          error.error.error?.translatedMessage || SNACK_MESSAGES.ERROR.FAIL_LOAD_INSTITUTIONS,
          SNACK_TYPES.ERROR
        );
      }
    } finally {
      this.isLoading = false;
    }
  }

  async onInstitutionChange(event: MatSelectChange) {
    const selectedInstitution = this.institutions.find(inst => inst.id === event.value);
    if (selectedInstitution) {
      this.selectedInstitutionId = event.value;

      try {
        this.isLoading = true;
        const resp = await this.healthMemberService.changeHealthMemberActiveInstitution(this.selectedInstitutionId)

        this.authService.setAccessToken(resp.token);
        this.cookiesService.setCookie('institutionId', resp.clinicId.toString(), environment.sessionExpiration);
        this.cookiesService.setCookie('institutionName', resp.clinicName, environment.sessionExpiration);

      } catch (error) {
        if (getAuthErrorMessage(error)) {
          this.snackBar.openSnackBar(
            SNACK_MESSAGES.ERROR.SESSION_EXPIRED,
            SNACK_TYPES.ERROR
          );
          setTimeout(() => {
            location.reload();
          }, 2000);
        } else {
          this.snackBar.openSnackBar(
            error.error.error?.translatedMessage || SNACK_MESSAGES.ERROR.FAIL_CHANGE_INSTITUTION,
            SNACK_TYPES.ERROR
          );
        }
      } finally {
        this.isLoading = false;
        window.location.reload();
      }
    }
  }

  get isLoggedIn(): boolean {
    return this.router.url.includes('app');
  }

  get isPatientFullRegistered(): boolean {
    return (
      this.authService.checkUserRole(this.roles.PATIENT) &&
      this.authService.isEmailVerified()
    );
  }

  get userName(): string {
    return this.cookiesService.getCookie('userName');
  }

  get userRole() {
    return this.authService.getUserRole();
  }
}
